import React, { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';

import styles from './lesson-player.style';
import { ActivityPlayerEvent } from '../../enums/activity-player-event.enum';
import { useLessonContext } from '../../hooks/lesson-player.context';
import {
  ActivityProvider,
  useActivityContext,
} from '../../hooks/use-activity.context';
import { useKeyPressWithHandler } from '../../hooks/use-key-press-with-handler';
import { LessonSettingsProvider } from '../../hooks/use-lesson-settings';
import { usePrevious } from '../../hooks/use-previous';
import type { NavHeader } from '../../interfaces/nav-header';
import { ActivityIdViewer } from '../activity-id-viewer/activity-id-viewer.component';
import { PlayerViewerSelector } from '../player-viewers/player-viewer-selector';
import { PlayerViewerType } from '../player-viewers/player-viewer-type.enum';

interface LessonPlayerProps {
  id: number;
  slides: any[];
  rawData: Record<string, unknown>;
  viewerType?: PlayerViewerType;
  navHeader?: NavHeader;
}

function LessonPlayer({
  slides,
  viewerType = PlayerViewerType.ISC,
  navHeader,
}: LessonPlayerProps) {
  const { logEvent } = useLessonContext();
  const { currentIndex, currentActivity, completions } = useActivityContext();

  const prevCompletions = usePrevious<boolean[]>(completions);

  const [isActivityIdVisible, setActivityIdVisible] = useState(false);

  const keyPressHandler = useCallback((event: KeyboardEvent) => {
    if (event.type !== 'keydown') {
      return;
    }

    if (event.key === '.' && event.ctrlKey) {
      setActivityIdVisible((isVisible) => !isVisible);
    }
  }, []);

  useEffect(() => {
    logEvent({ type: ActivityPlayerEvent.SLIDE_CHANGED, index: currentIndex });
  }, [currentIndex, logEvent]);

  useKeyPressWithHandler(keyPressHandler);

  useEffect(() => {
    if (
      completions[currentIndex] === true &&
      prevCompletions?.[currentIndex] !== true
    ) {
      logEvent({
        type: ActivityPlayerEvent.ACTIVITY_COMPLETE,
        index: currentIndex,
      });
    }
  }, [completions, logEvent, currentIndex, prevCompletions]);

  useEffect(() => {
    if (!!completions?.length && completions.every((c) => c)) {
      logEvent({ type: ActivityPlayerEvent.LESSON_COMPLETE });
    }
  }, [completions, logEvent]);

  return (
    <View style={styles.container}>
      <PlayerViewerSelector
        type={viewerType}
        slides={slides}
        navHeader={navHeader}
      />
      {isActivityIdVisible && currentActivity?.elementId && (
        <ActivityIdViewer id={currentActivity?.elementId} />
      )}
    </View>
  );
}

export default (props) => (
  <ActivityProvider {...props}>
    <LessonSettingsProvider {...props}>
      <LessonPlayer {...props} />
    </LessonSettingsProvider>
  </ActivityProvider>
);
